import React, { useMemo, useCallback } from 'react';
import { ArrowDownIcon } from '../Icons';
import VirtualizedTableBody from '../VirtualizedTableBody';
import {
  fieldLabelsDashboard,
  isDateField,
  isTagsField,
  isRiskScoreField,
  getRiskScoreLabel,
  getTextColorForBackground,
  getRiskScoreColor,
  formatDate,
  getComputerTypeOptions
} from '../utils';

const DataTable = React.memo(({ 
  title, 
  records, 
  tagsOptions, 
  sortField, 
  sortOrder, 
  filters, 
  passwordVisibility, 
  hoveredLink, 
  hoveredEye,
  showTagsDropdown,
  datePopover,
  onSort,
  onFilterChange,
  onDateFilterChange,
  onTagsFilterChange,
  onTogglePasswordVisibility,
  onHoveredLink,
  onHoveredEye,
  onShowTagsDropdown,
  onDatePopover,
  currentUserId,
  onSolvedToggle,
  onRiskScoreChange,
  onComputerTypeChange,
  userCredentialVisibility,
  onTagAdded,
  onTagRemoved
}) => {
  const tagIdToName = useMemo(() => {
    const map = {};
    tagsOptions.forEach((tag) => {
      map[String(tag.id)] = tag.name;
    });
    return map;
  }, [tagsOptions]);

  const getTagColor = useCallback((tagId) => {
    const tagIdStr = String(tagId);
    for (const tag of tagsOptions) {
      if (String(tag.id) === tagIdStr) {
        return tag.color;
      }
    }
    return '#e5e7eb';
  }, [tagsOptions]);

  const getTagPriority = useCallback((tagId) => {
    const tagIdStr = String(tagId);
    const tag = tagsOptions.find(t => String(t.id) === tagIdStr);
    return tag ? tag.priority : 0;
  }, [tagsOptions]);

  return (
    <>
      <style>{`
        .custom-scrollbar {
          scrollbar-width: thin;
          scrollbar-color: #6b7280 #374151;
        }
        .custom-scrollbar::-webkit-scrollbar {
          width: 8px;
          height: 8px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: #374151;
          border-radius: 4px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #6b7280;
          border-radius: 4px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #9ca3af;
        }
        .custom-scrollbar::-webkit-scrollbar-corner {
          background: #374151;
        }
      `}</style>
      <div style={{ marginBottom: '2rem' }}>
        <h2 style={{ color: 'var(--text)', margin: '0 0 1rem 0', fontSize: '1.1rem' }}>{title}</h2>
        <div style={{ background: 'var(--white)', borderRadius: '8px', boxShadow: '0 1px 2px rgba(0, 0, 0, 0.07)', border: '1px solid var(--border)', overflow: 'visible' }}>
          <div style={{ padding: '0.5rem 0.5rem 0.5rem 0.5rem', borderBottom: '1px solid var(--border)', background: 'var(--background)', display: 'flex', flexWrap: 'wrap', gap: '0.7rem' }}>
            {Object.keys(fieldLabelsDashboard).filter(field => field !== 'domain_type').map((field) => (
              <div key={field} style={{ minWidth: 90, maxWidth: 160, position: 'relative', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
                {isDateField(field) ? (
                  <>
                    <button
                      type="button"
                      className="form-input date-button"
                      style={{
                        minWidth: 90,
                        fontSize: 12,
                        padding: '0.1rem 0.4rem',
                        cursor: 'pointer',
                        background: '#374151',
                        color: 'var(--text)',
                        textAlign: 'left',
                        height: 28,
                        border: '2px solid var(--border)',
                        borderRadius: 8,
                        marginTop: 0,
                        marginBottom: 0,
                        boxSizing: 'border-box',
                        display: 'flex',
                        alignItems: 'center',
                      }}
                      onClick={e => {
                        if (datePopover.field === field) {
                          onDatePopover({ field: null, anchor: null });
                        } else {
                          onDatePopover({ field, anchor: e.currentTarget });
                        }
                      }}
                    >
                      {filters[field]?.from || filters[field]?.to
                        ? `${filters[field]?.from || '...'} ~ ${filters[field]?.to || '...'}`
                        : `Any ${fieldLabelsDashboard[field]}`}
                    </button>
                    {datePopover.field === field && (
                      <div className="date-popover" style={{
                        position: 'absolute',
                        zIndex: 1000,
                        background: '#374151',
                        border: '1px solid var(--border)',
                        borderRadius: 6,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                        top: '100%',
                        left: 0,
                        minWidth: '100%',
                        padding: '0.7rem',
                      }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                          <label style={{ fontWeight: 500, color: 'var(--text-light)', fontSize: 11 }}>From</label>
                          <input type="date" className="form-input" style={{ fontSize: 12, padding: '0.1rem 0.4rem', height: 28 }} value={filters[field]?.from || ''} onChange={e => onDateFilterChange(field, 'from', e.target.value)} />
                          <label style={{ fontWeight: 500, color: 'var(--text-light)', fontSize: 11 }}>To</label>
                          <input type="date" className="form-input" style={{ fontSize: 12, padding: '0.1rem 0.4rem', height: 28 }} value={filters[field]?.to || ''} onChange={e => onDateFilterChange(field, 'to', e.target.value)} />
                          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6, marginTop: 6 }}>
                            <button className="btn btn-link" style={{ fontSize: 11 }} onClick={() => { onFilterChange(field, {}); onDatePopover({ field: null, anchor: null }); }}>Clear</button>
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                ) : isTagsField(field) ? (
                  <div style={{ position: 'relative' }}>
                    <label style={{ fontWeight: 500, color: 'var(--text-light)', fontSize: 11 }}>{fieldLabelsDashboard[field]}</label>
                    <div
                      className="form-input tags-button"
                      style={{
                        minWidth: 90,
                        fontSize: 12,
                        padding: '0.1rem 0.4rem',
                        cursor: 'pointer',
                        userSelect: 'none',
                        background: '#374151',
                        color: 'var(--text)',
                        height: 28,
                        border: '2px solid var(--border)',
                        borderRadius: 8,
                        marginTop: 0,
                        marginBottom: 0,
                        boxSizing: 'border-box',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                      }}
                      onClick={() => onShowTagsDropdown(!showTagsDropdown)}
                    >
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {filters.tags && filters.tags.length > 0
                          ? filters.tags
                              .filter((id) => tagIdToName[id]) // Only show tags that exist in user_tags
                              .map((id) => tagIdToName[id])
                              .join(', ') || 'All'
                          : 'All'}
                      </span>
                      <ArrowDownIcon />
                    </div>
                    {showTagsDropdown && (
                      <div className="tags-dropdown" style={{ position: 'absolute', zIndex: 1000, background: '#374151', border: '1px solid var(--border)', borderRadius: 6, boxShadow: '0 2px 8px rgba(0,0,0,0.3)', top: '100%', left: 0, minWidth: '100%' }}>
                        {tagsOptions.map((tag) => (
                          <label key={tag['id']} style={{ display: 'flex', alignItems: 'center', padding: '0.3rem 0.7rem', cursor: 'pointer', fontSize: 12 }}>
                            <input
                              type="checkbox"
                              checked={filters.tags && filters.tags.includes(tag['id'])}
                              onChange={() => onTagsFilterChange(tag['id'])}
                              style={{ marginRight: 6 }}
                            />
                            <span>{tag['name']}</span>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                ) : isRiskScoreField(field) ? (
                  <div>
                    <label style={{ fontWeight: 500, color: 'var(--text-light)', fontSize: 11 }}>{fieldLabelsDashboard[field]}</label>
                    <select
                      className="form-input"
                      style={{ minWidth: 70, fontSize: 12, padding: '0.1rem 0.4rem', height: 28 }}
                      value={filters[field] || ''}
                      onChange={e => onFilterChange(field, e.target.value)}
                    >
                      <option value="">All</option>
                      <option value="1">Low</option>
                      <option value="2">Medium</option>
                      <option value="3">High</option>
                      <option value="4">Very High</option>
                      <option value="5">Critical</option>
                    </select>
                  </div>
                ) : field === 'computer_type' ? (
                  <div>
                    <label style={{ fontWeight: 500, color: 'var(--text-light)', fontSize: 11 }}>{fieldLabelsDashboard[field]}</label>
                    <select
                      className="form-input"
                      style={{ minWidth: 70, fontSize: 12, padding: '0.1rem 0.4rem', height: 28 }}
                      value={filters[field] || ''}
                      onChange={e => onFilterChange(field, e.target.value)}
                    >
                      <option value="">All</option>
                      {getComputerTypeOptions().map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>
                ) : (
                  <div>
                    <label style={{ fontWeight: 500, color: 'var(--text-light)', fontSize: 11 }}>{fieldLabelsDashboard[field]}</label>
                    <input
                      type="text"
                      className="form-input"
                      style={{ minWidth: 70, fontSize: 12, padding: '0.1rem 0.4rem', height: 28 }}
                      value={filters[field] || ''}
                      onChange={e => onFilterChange(field, e.target.value)}
                      placeholder={`Search ${fieldLabelsDashboard[field]}`}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
          <div className="custom-scrollbar" style={{ overflowX: 'hidden', overflowY: 'visible'}}>
            <table style={{ width: '100%', tableLayout: 'fixed', borderCollapse: 'separate', borderSpacing: 0, fontSize: '0.92rem', borderRadius: 10, overflow: 'visible', border: '1px solid var(--border)', boxShadow: '0 2px 8px rgba(30,39,56,0.04)' }}>
              <thead style={{ position: 'sticky', top: 0, zIndex: 10 }}>
                <tr style={{ background: 'var(--background)', display: 'flex', width: '100%' }}>
                  <th style={{ width: 36, minWidth: 36, maxWidth: 36, background: 'var(--background)', borderBottom: '2px solid var(--border)', textAlign: 'center', padding: '0.7rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <i className="fas fa-clipboard-check" style={{ color: 'var(--text)', fontSize: '1rem' }} title="Solved Status"></i>
                  </th>
                  {Object.keys(fieldLabelsDashboard).filter(field => field !== 'domain_type').map((field) => (
                    <th
                      key={field}
                      className={
                        field === 'computer_type' ? 'col-computer_type' :
                        field === 'date' ? 'col-date' :
                        field === 'bucket' ? 'col-bucket' : ''
                      }
                      style={{
                        textAlign: 'left',
                        padding: '0.7rem',
                        color: 'var(--text)',
                        fontWeight: 700,
                        cursor: 'pointer',
                        userSelect: 'none',
                        fontSize: '1rem',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        background: 'var(--background)',
                        borderBottom: '2px solid var(--border)',
                        flex: isTagsField(field) ? 0.6 : 1,
                        maxWidth: isTagsField(field) ? '200px' : 'none'
                      }}
                      onClick={() => onSort(field)}
                    >
                      {fieldLabelsDashboard[field]}
                      {sortField === field && (
                        <span style={{ marginLeft: 3, color: 'var(--primary-color)' }}>{sortOrder === 'asc' ? '▲' : '▼'}</span>
                      )}
                    </th>
                  ))}
                  <th key="intelx-link" style={{ width: 36, minWidth: 36, maxWidth: 36, borderBottom: '2px solid var(--border)', background: 'var(--background)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}></th>
                </tr>
              </thead>
              {/* Virtualized body */}
            </table>
            {records.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '1.2rem', color: 'var(--text-light)', fontSize: '0.95rem', background: '#374151' }}>No records found.</div>
            ) : (
              <VirtualizedTableBody
                records={records}
                passwordVisibility={passwordVisibility}
                hoveredLink={hoveredLink}
                hoveredEye={hoveredEye}
                tagIdToName={tagIdToName}
                getTagColor={getTagColor}
                getTagPriority={getTagPriority}
                formatDate={formatDate}
                getRiskScoreLabel={getRiskScoreLabel}
                getRiskScoreColor={getRiskScoreColor}
                getTextColorForBackground={getTextColorForBackground}
                isDateField={isDateField}
                isTagsField={isTagsField}
                isRiskScoreField={isRiskScoreField}
                fieldLabelsDashboard={fieldLabelsDashboard}
                onTogglePasswordVisibility={onTogglePasswordVisibility}
                onHoveredLink={onHoveredLink}
                onHoveredEye={onHoveredEye}
                currentUserId={currentUserId}
                onSolvedToggle={onSolvedToggle}
                onRiskScoreChange={onRiskScoreChange}
                onComputerTypeChange={onComputerTypeChange}
                userCredentialVisibility={userCredentialVisibility}
                tagsOptions={tagsOptions}
                onTagAdded={onTagAdded}
                onTagRemoved={onTagRemoved}
              />
            )}
          </div>
        </div>
      </div>
    </>
  );
});

export default DataTable; 